import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { getProductReport } from '../api/product'
import { scansApi } from '../api/scans'
import { ranHeader } from '../utils/runTime'
import { AppShell } from '../components/layout/AppShell'
import { Spinner } from '../components/shared/Spinner'
import { RoadmapImpact } from '../components/product/RoadmapImpact'
import { SalesBlockers } from '../components/product/SalesBlockers'
import { GtmNarrativeBlock } from '../components/product/GtmNarrativeBlock'
import { GtmMethodologyPanel } from '../components/product/GtmMethodologyPanel'
import type { ProductMetrics } from '../types/api'
import { PageTitle } from '../components/shared/PageTitle'

export default function ProductReportPage() {
  const { scanId } = useParams<{ scanId: string }>()
  const navigate = useNavigate()
  const [isRegenerating, setIsRegenerating] = useState(false)

  // The scan row AppShell already reads (same key), for the run time in the header.
  const { data: scan } = useQuery({
    queryKey: ['scan', scanId],
    queryFn: () => scansApi.get(scanId!),
    enabled: !!scanId,
  })
  const { data: metrics, isLoading, error, refetch } = useQuery<ProductMetrics>({
    queryKey: ['product-report', scanId],
    queryFn: () => getProductReport(scanId!, false),
    enabled: !!scanId,
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data) return false
      if (data.gtm_narrative === null && data.narrative_error === null) return 3000
      return false
    },
  })

  const regenerateMutation = useMutation({
    mutationFn: () => getProductReport(scanId!, true),
    onMutate: () => setIsRegenerating(true),
    onSettled: () => {
      setIsRegenerating(false)
      refetch()
    },
  })

  if (isLoading) {
    return (
      <AppShell scanId={scanId} crumb="Product & GTM report" active="report">
        <div className="h-full flex items-center justify-center">
          <Spinner />
        </div>
      </AppShell>
    )
  }

  return (
    <AppShell scanId={scanId} crumb="Product & GTM report" active="report">
      <main className="max-w-4xl mx-auto w-full px-6 py-8 space-y-8">
        {error ? (
          <div className="rounded-lg border border-critical/30 bg-chip-critical p-6 text-center">
            <p className="text-[13px] text-critical">
              Could not load product report. Try refreshing the page.
            </p>
          </div>
        ) : metrics ? (
          <>
            {/* Header */}
            <div>
              <PageTitle className="mb-1">Product & GTM Report</PageTitle>
              <p className="text-[13px] text-muted">
                Go-to-market impact analysis · {metrics.sales_blockers.length} sales blockers · Generated{' '}
                {new Date(metrics.computed_at).toLocaleDateString()}
                {ranHeader(scan) && ` · ${ranHeader(scan)}`}
              </p>
            </div>

            {/* Roadmap impact */}
            <section>
              <p className="text-[11px] font-semibold text-muted uppercase tracking-wider mb-3">
                Roadmap & Velocity Impact
              </p>
              <RoadmapImpact metrics={metrics} />
            </section>

            {/* Sales blockers */}
            <section>
              <p className="text-[11px] font-semibold text-muted uppercase tracking-wider mb-3">
                Enterprise Sales Blockers
              </p>
              <SalesBlockers blockers={metrics.sales_blockers} />
            </section>

            {/* GTM narrative */}
            <section>
              <p className="text-[11px] font-semibold text-muted uppercase tracking-wider mb-3">
                GTM Summary
              </p>
              <div className="border border-hairline rounded-lg shadow-card bg-panel p-5">
                <GtmNarrativeBlock
                  narrative={metrics.gtm_narrative}
                  generatedAt={metrics.narrative_generated_at}
                  error={metrics.narrative_error}
                  isLoading={metrics.gtm_narrative === null && metrics.narrative_error === null}
                  onRegenerate={() => regenerateMutation.mutate()}
                  isRegenerating={isRegenerating}
                />
              </div>
            </section>

            {/* Methodology */}
            <section>
              <div className="border border-hairline rounded-lg shadow-card bg-panel p-4">
                <GtmMethodologyPanel metrics={metrics} />
                <div className="mt-4 pt-3 border-t border-hairline">
                  <button
                    onClick={() => navigate(`/scans/${scanId}/findings`)}
                    className="text-[13px] text-accent-text hover:underline"
                  >
                    View technical findings →
                  </button>
                </div>
              </div>
            </section>
          </>
        ) : null}
      </main>
    </AppShell>
  )
}
